import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { usePublicRooms } from "@/features/rooms/hooks/usePublicRooms";
import { formatArea } from "@/lib/utils/formatters";
import { cn } from "@/lib/utils/cn";

export default function ChooseStay() {
  const { data: rooms, isLoading } = usePublicRooms();
  const [activeIdx, setActiveIdx] = useState(0);

  if (isLoading) {
    return (
      <section id="rooms" className="bg-white py-16 md:py-24">
        <div className="container-narrow">
          <p className="eyebrow mb-3">Explore</p>
          <h2 className="mb-10 font-serif text-display-md text-metro-green-950">
            Choose Your Stay
          </h2>
          <div className="grid items-stretch gap-8 lg:grid-cols-[1.4fr_1fr] lg:gap-10">
            <div className="h-[320px] animate-pulse rounded-2xl bg-metro-cream-100 md:h-[420px] lg:h-[460px]" />
            <div className="space-y-4 flex flex-col justify-center">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="space-y-2 border-l-[3px] border-transparent pl-6"
                >
                  <div className="h-5 w-36 animate-pulse rounded bg-metro-cream-100" />
                  <div className="h-3 w-56 animate-pulse rounded bg-metro-cream-100" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!rooms?.length) return null;

  const active = rooms[activeIdx];
  const cover = active?.images?.[0];

  return (
    <section id="rooms" className="bg-white py-16 md:py-24">
      <div className="container-narrow">
        <p className="eyebrow mb-3">Explore</p>
        <h2 className="mb-10 font-serif text-display-md text-metro-green-950">
          Choose Your Stay
        </h2>

        <div className="grid items-stretch gap-8 lg:grid-cols-[1.4fr_1fr] lg:gap-10">
          <div className="relative h-[280px] overflow-hidden rounded-2xl bg-metro-cream-100 md:h-[380px] lg:h-[460px]">
            {cover ? (
              <img
                src={cover}
                alt={active.name}
                loading="lazy"
                className="h-full w-full object-cover transition-all duration-500"
              />
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-metro-ink-subtle">
                <p className="text-sm">No image uploaded yet</p>
              </div>
            )}
          </div>

          <div className="flex flex-col justify-center">
            <ul className="divide-y divide-metro-cream-100">
              {rooms.map((room, idx) => {
                const isActive = idx === activeIdx;
                return (
                  <li key={room.id}>
                    <button
                      type="button"
                      onClick={() => setActiveIdx(idx)}
                      className={cn(
                        "group w-full py-4 pl-5 text-left transition md:py-5 md:pl-6",
                        isActive
                          ? "border-l-[3px] border-metro-green-800"
                          : "border-l-[3px] border-transparent",
                      )}
                    >
                      <div
                        className={cn(
                          "mb-1 font-serif text-lg text-metro-green-950 transition md:text-xl",
                          !isActive && "opacity-60 group-hover:opacity-100",
                        )}
                      >
                        {room.name}
                      </div>
                      <div
                        className={cn(
                          "text-xs text-metro-ink-muted transition md:text-sm",
                          !isActive && "opacity-50",
                        )}
                      >
                        {formatArea(room.size)} · {room.maxGuests} Guests ·{" "}
                        {room.bedConfig} · {room.bathrooms}{" "}
                        {room.bathrooms === 1 ? "Bathroom" : "Bathrooms"}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>

            <div className="mt-6 pl-5 md:mt-8 md:pl-6">
              <Link
                to="/rooms"
                className="inline-flex items-center gap-2 rounded-full border border-metro-green-800 px-5 py-2.5 text-sm font-medium text-metro-green-800 transition hover:-translate-y-0.5 hover:bg-metro-green-800 hover:text-white md:px-6 md:py-3"
              >
                View All Rooms <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}